import { IDealDirectory, IDealDocument } from 'views/DocumentLibrary/types';
import { BLOCK_BOUNDARY_TAGS } from './constants';
import { TimelineItem, TimelineSection } from './types';

export const findDocumentInTree = (tree: IDealDirectory | null, docId: number): IDealDocument | null => {
    if (!tree) return null;

    const found = tree.documents?.find((d: IDealDocument) => d.id === docId);
    if (found) return found;

    if (tree.children) {
        for (const child of tree.children) {
            const result = findDocumentInTree(child, docId);
            if (result) return result;
        }
    }

    return null;
};

// The API returns a numeric id; coerce to string to match TimelineItem.id and the ids used in URLs.
export const normalizeTimelineId = (timeline: TimelineItem): TimelineItem => (
    { ...timeline, id: String(timeline.id) }
);

/**
 * Strips HTML tags from a string and returns plain text.
 * Creates a temporary DOM element to parse HTML and extract text content.
 */
export const stripHtmlTags = (html: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
};

/**
 * Single-line plain text for a comment body. Stripping tags alone returns `textContent`,
 * which runs the blocks together, so block boundaries become a space and every whitespace run collapses to one.
 */
export const htmlToPlainText = (html: string): string => stripHtmlTags(html.replace(BLOCK_BOUNDARY_TAGS, '$& '))
    .replace(/\s+/g, ' ')
    .trim();

/** Activities that don't belong to a section. They render above the sections in the UI. */
export const getUngroupedTimelines = (timelines: TimelineItem[]): TimelineItem[] => (
    timelines.filter(item => item.sectionId == null)
);

/** Activities keyed by section id, each list keeping the order the API returned them in. */
export const groupTimelinesBySection = (timelines: TimelineItem[]): Map<number, TimelineItem[]> => {
    const grouped = new Map<number, TimelineItem[]>();
    timelines.forEach(item => {
        if (item.sectionId == null) return;
        const sectionItems = grouped.get(item.sectionId) ?? [];
        sectionItems.push(item);
        grouped.set(item.sectionId, sectionItems);
    });
    return grouped;
};

/** Sections in the given id order, dropping ids that no longer resolve to a section. */
export const orderSections = (sections: TimelineSection[], orderedSectionIds: number[]): TimelineSection[] => {
    const byId = new Map(sections.map(section => [section.id, section]));
    return orderedSectionIds
        .map(id => byId.get(id))
        .filter((section): section is TimelineSection => Boolean(section));
};